import React, {useRef, useState, useEffect} from "react";

import "./Sidebar.css";
import {Alert, ModalFooter} from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import {faX, faCheck, faSpinner, faWifi, faKey} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import InputGroup from "react-bootstrap/InputGroup";
import CustomRequests from "../CustomRequests";
import LoadingIcon from "../dasboardv3/components/Partials/LoadingIcon/LoadingIcon";
import {checkVersion, sleep} from "../../lib/utils";
import VersionInfo from "./VersionInfo/VersionInfo";
import BondApi from "../../lib/api/bond";
import LogLevels from "./LogLevels";
import LivelogConnection from "./LivelogConnection";
import Dropdown from "react-bootstrap/Dropdown";
import { useAuth } from "../../contexts/AuthContext";

window.isReloadingBond = false;

function Sidebar({
                     bondId,
                     setBondId,
                     clearBondId,
                     bondApi,
                     setBondApi,
                     setMessages,
                     setCurrentTab,
                     bondToken,
                     setBondToken,
                     bondApiType,
                     isSetupMode,
                     setIsSetupMode
                 }) {
    const { getToken, loading: authLoading, isAuthenticated } = useAuth();
    const bondIdRef = useRef(bondId);
    const bondTokenRef = useRef(bondToken);
    const [bondVersionData, setBondVersionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [livelogCompatible, setLivelogCompatible] = useState(false);

    useEffect(() => {
        if (bondId && !isSetupMode) {
            reloadBond();
        }
    }, [bondApi, isSetupMode]);

    useEffect(() => {
        if (bondId) bondIdRef.current.value = bondId;
        if (bondToken) bondTokenRef.current.value = bondToken;
    }, [bondId, bondToken]);

    useEffect(() => {
        // Wait for authentication to complete before loading bond for cloud API
        if (authLoading) {
            return; // Don't load until auth is ready
        }

        // For cloud API, require authentication
        if (bondApiType === "cloud" && !isAuthenticated) {
            return; // Don't load without authentication
        }

        // For cloud API, ensure token is available
        if (bondApiType === "cloud") {
            const token = getToken();
            if (!token) {
                return;
            }
        }

        if (!isSetupMode && bondId && (bondToken || bondApiType === "cloud")) {
            loadBond();
        }
    }, [isSetupMode, bondId, bondToken, bondApiType, authLoading, isAuthenticated, getToken]);

    async function loadBond() {
        if (!bondId) {
            bondId = bondIdRef.current.value;
            setBondId(bondId);
        }

        if (!bondToken && bondTokenRef.current && !isSetupMode) {
            bondToken = bondTokenRef.current.value;
            setBondToken(bondToken);
        }

        // Get JWT token for authenticated API requests
        const jwtToken = getToken();

        // For cloud API, ensure we have a JWT token
        if (bondApiType === "cloud" && !jwtToken) {
            return;
        }

        window.api = new BondApi(bondId, isSetupMode ? null : bondToken, bondApiType, jwtToken);

        setIsLoading(true);
        setConnectionError(null);
        setBondVersionData(null);
        setLivelogCompatible(false);
        if (bondApiType === "local") await sleep(800);
        reloadBond();
    }

    function reloadBond() {
        if (window.isReloadingBond) return;
        window.isReloadingBond = true;
        window.api.get("/sys/version").then((versionData) => {
            if (!versionData.fw_ver) throw new Error("No firmware version on response");
            if (!isSetupMode) setLivelogCompatible(checkVersion(versionData.fw_ver, "4.10"));
            setBondVersionData(versionData);
            setBondApi(window.api);
        }).catch((e) => {
            setConnectionError(["⚠️ Bond is unreachable ⚠️", "Check your connection and Bond ID."]);
            setBondId(null);
            console.error(e);
        }).finally(() => {
            setIsLoading(false);
            window.isReloadingBond = false;
        })
    }

    async function onSubmitBondData() {
        let _bondId = bondIdRef.current.value.toUpperCase();
        let _bondToken = bondTokenRef.current.value;

        setBondId(_bondId);
        setBondToken(isSetupMode ? null : _bondToken);
        await loadBond();
    }

    return (<Row>

        <InputGroup className="">
            <InputGroup.Text className={"sidebarLabel"}>
                Bond ID
            </InputGroup.Text>
            <Form.Control
                name="bondIdSelect"
                autoComplete="on"
                type="text"
                placeholder="..."
                aria-label="..."
                enterKeyHint="done"
                disabled={bondId || isLoading}
                style={{textTransform: "uppercase"}}
                ref={bondIdRef}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onSubmitBondData();
                    }
                }}
            />
            {!bondId || isLoading ? <Button
                variant="outline-primary"
                disabled={isLoading}
                className="submitBondBtn"
                onClick={() => {
                    onSubmitBondData()
                }}>{isLoading ? <FontAwesomeIcon className={"fa-spin"} icon={faSpinner} size="lg"/> :
                <FontAwesomeIcon icon={faCheck} size="lg"/>}
            </Button> : <Button
                variant="outline-primary"
                className="submitBondBtn"
                onClick={() => {
                    clearBondId()
                }}><FontAwesomeIcon icon={faX} size="lg"/>
            </Button>}
        </InputGroup>

        <InputGroup hidden={bondApiType === "cloud"} className={"inputLocalApiMode"}>
            <Dropdown>
                <Dropdown.Toggle variant="light" id="dropdown-basic" disabled={bondId || isLoading}
                >
                    {!isSetupMode ? <><FontAwesomeIcon icon={faKey} size="sm"/> Token</> : <>
                        <FontAwesomeIcon icon={faWifi} size="sm"/> Setup</>}

                </Dropdown.Toggle>

                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => {
                        setIsSetupMode(false)
                    }}><FontAwesomeIcon icon={faKey} size="md"/> Token</Dropdown.Item>
                    <Dropdown.Item onClick={() => {
                        setIsSetupMode(true)
                    }}><FontAwesomeIcon icon={faWifi} size="md"/> Setup</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            <Form.Control
                hidden={isSetupMode}
                name="bondTokenSelect"
                autoComplete="on"
                enterKeyHint="done"
                disabled={bondId || isLoading}
                type={"password"}
                ref={bondTokenRef}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onSubmitBondData();
                    }
                }}
            />
            <Form.Control
                hidden={!isSetupMode}
                disabled={true}
                value={"Connect on Config Wifi"}
            />

        </InputGroup>

        {isLoading ? <Row className={"livelogLoadingRow"}>
            <LoadingIcon className={"livelogLoading"}/>
        </Row> : ""}

        {connectionError ? <Alert variant="danger" onClose={() => setConnectionError(null)} dismissible>
            <h4 className={"text-center text-uppercase"}>{connectionError[0]}</h4>
            <br/>
            <p>{connectionError[1]}</p>
            <ModalFooter className={"text-lg-end text-uppercase text"}>{bondIdRef.current.value}</ModalFooter>
        </Alert> : <></>}

        {bondApi ? <>
            {bondVersionData ? <VersionInfo versionData={bondVersionData} bond={bondApi}/> : <></>}
            {bondVersionData ? <CustomRequests bondApi={bondApi}></CustomRequests> : <></>}

            {(bondVersionData && !isSetupMode) && (livelogCompatible ? <><LogLevels
                bondApi={bondApi}/>
                <LivelogConnection setMessages={setMessages} bondId={bondId} setCurrentTab={setCurrentTab}/>
            </> : <Alert variant={"warning"} dismissible>
                <p>Bond Version Incompatible</p>
                <p>Livelog requires firmware version 4.10 or higher</p>
            </Alert>)}


        </> : <></>}
    </Row>)
}

export default Sidebar;


